import React from "react";
import {Helmet} from "react-helmet";
import {useStaticQuery, graphql} from "gatsby";
import {useLocation} from "@reach/router";

interface SEOProps {
    title?: string;
    description?: string;
    image?: string;
    article?: boolean;
    children?: React.ReactNode;
    languageAlternatives?: LanguageAlternative[];
}

interface LanguageAlternative {
    lang: string;
    path: string;
    isDefault?: boolean;
}

export const SEO: React.FC<SEOProps> = ({
                                            title,
                                            description,
                                            image,
                                            article = false,
                                            children,
                                            languageAlternatives = [],
                                        }) => {
    const {pathname} = useLocation();

    const {site} = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          defaultTitle: title
          defaultDescription: description
          siteUrl
          defaultImage: image
          organization {
            name
            url
            logo
          }
        }
      }
    }
  `);

    const {
        defaultTitle,
        defaultDescription,
        siteUrl,
        defaultImage,
        twitterUsername,
        organization,
    } = site.siteMetadata;

    const seo = {
        title: title || defaultTitle,
        description: description || defaultDescription,
        image: `${siteUrl}${image || defaultImage}`,
        url: `${siteUrl}${pathname}`,
    };

    // Schema.org structured data
    const schemaOrgWebPage = {
        "@context": "https://schema.org",
        "@type": article ? "Article" : "WebPage",
        url: seo.url,
        headline: seo.title,
        description: seo.description,
        image: seo.image,
        publisher: {
            "@type": "Organization",
            name: organization.name,
            url: organization.url,
            logo: {
                "@type": "ImageObject",
                url: organization.logo,
            },
        },
    };

    return (
        <Helmet title={seo.title}>
            <html lang="en"/>
            <meta name="description" content={seo.description}/>
            <meta name="image" content={seo.image}/>
            <meta name="viewport" content="width=device-width, initial-scale=1.0"/>

            {/* Facebook/Open Graph */}
            <meta property="og:url" content={seo.url}/>
            <meta property="og:type" content={article ? "article" : "website"}/>
            <meta property="og:title" content={seo.title}/>
            <meta property="og:description" content={seo.description}/>
            <meta property="og:image" content={seo.image}/>

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image"/>
            <meta name="twitter:creator" content={twitterUsername}/>
            <meta name="twitter:title" content={seo.title}/>
            <meta name="twitter:description" content={seo.description}/>
            <meta name="twitter:image" content={seo.image}/>

            {/*/!* Language alternatives *!/*/}
            {languageAlternatives.map(({lang, path, isDefault}) => {

                if (path.startsWith("/ro/")) {

                    return <link
                        key={lang}
                        rel="alternate"
                        hrefLang={lang}
                        href={`${siteUrl}${path}/`}
                        data-custom="keep"
                    />
                }

               else if (path.startsWith("/en/")) {
                    return <link
                        key={lang}
                        rel="alternate"
                        hrefLang={lang}
                        href={`${siteUrl}${path}/`}
                        data-custom="keep"
                    />
                }

               else {
                    return <link
                        key={lang}
                        rel="alternate"
                        hrefLang={lang}
                        href={`${siteUrl}${path}/`}
                        data-custom="keep"
                    />

                }


            })}


            {languageAlternatives.map(({lang, path, isDefault}) => {

                if (isDefault) {
                    return <link
                        key={lang}
                        data-custom="keep"
                        rel="alternate"
                        hrefLang={"x-default"}
                        href={`${siteUrl}${path}/`}
                    />
                }



            })}


            {/* Schema.org structured data */}
            <script type="application/ld+json">
                {JSON.stringify(schemaOrgWebPage)}
            </script>

            {children}
        </Helmet>
    );
};

export default SEO;
